import { useState, useCallback } from 'react'
import { X, Copy } from 'lucide-react'
import { BaseModal } from '@/components/Modal/BaseModal'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { useRemoveFriend, useBlockUser } from '@/hooks/useFriends'
import { Friend } from '@/api/friends'

interface ManageFriendsModalProps {
  isOpen: boolean
  onClose: () => void
  inviteUrl?: string
  friends: Friend[]
}

interface FriendData {
  id: string
  friendId: string
  name: string
  initials: string
}

// Generate a consistent color for avatars based on name
function getAvatarColor(name: string): string {
  const colors = [
    'bg-orange-600', // #D35400
    'bg-purple-800', // #6B2C5F
    'bg-blue-900',   // #1B4F72
    'bg-gray-600',   // #666
    'bg-amber-700',  // #8B4513
    'bg-indigo-600',
    'bg-green-700',
    'bg-red-700',
  ]
  const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return colors[index % colors.length]
}

export function ManageFriendsModal({
  isOpen,
  onClose,
  inviteUrl = 'hub.imaginesports.com/invite/ABC123',
  friends = []
}: ManageFriendsModalProps) {
  const [isCopied, setIsCopied] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    type: 'remove' | 'block' | null
    friend: FriendData | null
  }>({ isOpen: false, type: null, friend: null })
  
  const removeFriend = useRemoveFriend()
  const blockUser = useBlockUser()

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch {
      // Fallback for browsers that don't support clipboard API
      const input = document.createElement('input')
      input.value = inviteUrl
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    }
  }, [inviteUrl])

  const handleAction = (type: 'remove' | 'block', friend: FriendData) => {
    setConfirmDialog({ isOpen: true, type, friend })
  }

  const handleConfirmAction = () => {
    if (confirmDialog.type && confirmDialog.friend) {
      if (confirmDialog.type === 'remove') {
        removeFriend.mutate(confirmDialog.friend.friendId)
      } else if (confirmDialog.type === 'block') {
        blockUser.mutate(confirmDialog.friend.friendId)
      }
    }
    setConfirmDialog({ isOpen: false, type: null, friend: null })
  }

  const handleCancelAction = () => {
    setConfirmDialog({ isOpen: false, type: null, friend: null })
  }

  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={onClose}
        title=""
        size="lg"
        showCloseButton={false}
        className="p-0 overflow-hidden max-w-[720px] max-sm:max-w-[calc(100vw-32px)]"
      >
        {/* Modal Header */}
        <div className="px-8 max-sm:px-4 py-6 max-sm:py-4 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between">
            <h2 className="font-bebas-neue text-[42px] max-sm:text-[32px] leading-none tracking-[2px] uppercase m-0">
              Manage Friends
            </h2>
            <button
              onClick={onClose}
              className={cn(
                'w-12 h-12 max-sm:w-10 max-sm:h-10 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600',
                'flex items-center justify-center transition-all duration-200 flex-shrink-0',
                'hover:rotate-90'
              )}
              aria-label="Close modal"
            >
              <X className="h-6 w-6 max-sm:h-5 max-sm:w-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="px-8 max-sm:px-4 py-6 max-sm:py-4 max-h-[calc(90vh-120px)] overflow-y-auto">
          {/* Invite Section */}
          <div className="mb-12">
            <h3 className="font-oswald text-xl font-semibold uppercase tracking-[1px] mb-4">
              Invite Friends
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <input
                type="text"
                value={inviteUrl}
                readOnly
                className="flex-1 min-w-0 px-5 max-sm:px-3 py-3.5 max-sm:py-2.5 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-base max-sm:text-sm font-mono text-gray-700 dark:text-gray-200 truncate"
              />
              <Button
                variant="outline"
                size="lg"
                onClick={handleCopyLink}
                className={cn(
                  'gap-2 px-6 border-2 flex-shrink-0',
                  'font-oswald text-base font-medium uppercase tracking-[0.5px]',
                  'transition-all duration-200',
                  isCopied && 'bg-green-500 text-white border-green-500 hover:bg-green-500 hover:text-white'
                )}
              >
                <Copy className="h-5 w-5" />
                <span>{isCopied ? 'Copied!' : 'Copy Link'}</span>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground italic">
              Send via email, text message, or post to social media
            </p>
          </div>

          {/* Friends List */}
          <div>
            <h3 className="font-oswald text-xl font-semibold uppercase tracking-[1px] mb-4">
              Your Friends
            </h3>
            <p className="font-oswald text-base font-medium uppercase tracking-[0.5px] text-gray-600 dark:text-gray-400 mb-4">
              {friends.length} friends
            </p>

            {friends.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-6xl mb-6 opacity-30">👥</div>
                <h4 className="font-bebas-neue text-4xl tracking-[1px] uppercase mb-2 text-gray-600 dark:text-gray-400">
                  No Friends Yet
                </h4>
                <p className="font-oswald text-base text-gray-500 dark:text-gray-400">
                  Invite friends to start competing!
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {friends.map((friend) => {
                  const friendData = friend.friend
                  const name = friendData.firstName && friendData.lastName 
                    ? `${friendData.firstName} ${friendData.lastName}`
                    : friendData.email
                  const initials = friendData.firstName && friendData.lastName
                    ? `${friendData.firstName[0]}${friendData.lastName[0]}`.toUpperCase()
                    : friendData.email.slice(0, 2).toUpperCase()
                  
                  const friendInfo: FriendData = {
                    id: friend.id,
                    friendId: friend.friendId,
                    name,
                    initials
                  }
                  
                  return (
                    <div
                      key={friend.id}
                      className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-xl transition-colors duration-200"
                    >
                      <Avatar className="h-12 w-12 mr-4">
                        <AvatarFallback 
                          className={cn(
                            'text-white font-semibold text-lg',
                            getAvatarColor(name)
                          )}
                        >
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-oswald text-lg font-semibold">
                          {name}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAction('remove', friendInfo)}
                          className={cn(
                          'px-4 py-2 rounded-md',
                          'font-oswald text-[13px] font-medium uppercase tracking-[0.5px]',
                          'bg-white dark:bg-gray-600 text-gray-600 dark:text-gray-200 border border-gray-200 dark:border-gray-500',
                          'hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 hover:border-red-600 dark:hover:border-red-500',
                          'transition-all duration-200'
                        )}
                      >
                        Remove
                      </button>
                      <button
                        onClick={() => handleAction('block', friendInfo)}
                        className={cn(
                          'px-4 py-2 rounded-md',
                          'font-oswald text-[13px] font-medium uppercase tracking-[0.5px]',
                          'bg-white dark:bg-gray-600 text-gray-600 dark:text-gray-200 border border-gray-200 dark:border-gray-500',
                          'hover:bg-gray-800 hover:text-white hover:border-gray-800 dark:hover:bg-gray-900 dark:hover:border-gray-700',
                          'transition-all duration-200'
                        )}
                      >
                        Block
                      </button>
                    </div>
                  </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </BaseModal>

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && confirmDialog.friend && (
        <BaseModal
          isOpen={confirmDialog.isOpen}
          onClose={handleCancelAction}
          title=""
          size="sm"
          showCloseButton={false}
          className="p-8"
        >
          <h3 className="font-bebas-neue text-[28px] tracking-[1px] uppercase mb-4">
            {confirmDialog.type === 'remove' ? 'Remove Friend?' : 'Block Friend?'}
          </h3>
          <p className="text-base text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
            {confirmDialog.type === 'remove'
              ? `Remove ${confirmDialog.friend.name} as a friend? They won't be notified that you removed them.`
              : `Block ${confirmDialog.friend.name}? They won't be notified that you blocked them. This will also prevent them from sending you friend requests in the future.`}
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={handleCancelAction}
              className="font-oswald uppercase tracking-[0.5px]"
            >
              Cancel
            </Button>
            <Button
              variant={confirmDialog.type === 'remove' ? 'destructive' : 'default'}
              onClick={handleConfirmAction}
              className={cn(
                'font-oswald uppercase tracking-[0.5px]',
                confirmDialog.type === 'block' && 'bg-gray-900 hover:bg-gray-800'
              )}
            >
              {confirmDialog.type === 'remove' ? 'Remove' : 'Block'}
            </Button>
          </div>
        </BaseModal>
      )}
    </>
  )
}